import React, { FC, useCallback } from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { DatePicker, FieldWrapper } from '@td-client-frontend/common';
import { Add } from '@td-client-frontend/icons';
import PlansCheckbox from '../components/PlansCheckbox';
import Truncatebale from '../components/Truncatebale';
import ResizableTextArea from '../components/ResizableTextArea';
import { planDateFormatter } from '../../../../utils/plansModalUtils';
import { Plan, PlanError } from '../interfaces/PlanIntrfaces';

interface EditPlansProps {
  plan: Plan;
  fieldsErrors: PlanError[];
  bgColor: string;
  showCheckbox: boolean;
  checkedPlan: boolean;
  disabledField: boolean;
  setPlanChecked: ({ newCheckedValue, plan }: { newCheckedValue: string; plan: Plan }) => void;
  deletePlan: (planId: string, isItInitialFields: boolean) => void;
  editFields: (id: string, key: string, value: string) => void;
}

const EditPlans: FC<EditPlansProps> = ({
  plan,
  fieldsErrors,
  deletePlan,
  editFields,
  bgColor,
  showCheckbox,
  checkedPlan,
  setPlanChecked,
  disabledField
}) => {
  const onDelete = useCallback(() => {
    deletePlan(plan.id, disabledField);
  }, [deletePlan, disabledField, plan.id]);

  const editCodeHandler = useCallback(
    (value: string) => {
      editFields(plan.id, 'code', value);
    },
    [editFields, plan.id]
  );

  const editDateHandler = useCallback(
    (date: Date, type: string) => {
      const dateValue = planDateFormatter(date);
      editFields(plan.id, type, dateValue);
    },
    [editFields, plan.id]
  );

  const codeError = fieldsErrors?.find((error) => error.id === plan.id && error.type === 'CODE');
  const startDateError = fieldsErrors?.find((error) => error.id === plan.id && error.type === 'START');

  return (
    <div
      className={`grid gap-4 border-b-1 border-gray-20 py-2 ${bgColor}`}
      style={{
        gridTemplateColumns: '0.5fr 2fr repeat(2, minmax(0, 1.5fr)) 0.5fr'
      }}
    >
      <PlansCheckbox
        checkedValue={checkedPlan}
        onCheckedChange={setPlanChecked}
        plan={plan}
        showCheckbox={showCheckbox}
      />
      <Truncatebale>
        <FieldWrapper
          error={codeError && codeError.reason}
          isError={!!codeError}
          name={`code-${plan.id}`}
          className="flex flex-col w-full"
          fieldLabelClasses="hidden"
          errorClassName={!codeError && !startDateError ? 'hidden' : ''}
        >
          {disabledField ? (
            <p className="font-effra font-normal text-gray-default pt-4 pr-3 pb-3">{plan.code}</p>
          ) : (
            <ResizableTextArea
              classes={clsx(
                codeError ? 'border-red-500 border-2' : 'border-gray-default border',
                'flex flex-col bg-white p-4 leading-none resize-none rounded-lg appearance-none font-normal font-effra text-black-default focus:outline-none active:outline-none focus:border-2 focus:border-black'
              )}
              value={plan.code || ''}
              name={plan.id}
              onChange={editCodeHandler}
              maxHeight={8}
            />
          )}
        </FieldWrapper>
      </Truncatebale>
      <Truncatebale>
        <DatePicker
          value={moment(plan.start_dt).isValid() ? moment(plan.start_dt).format('MM/DD/YYYY') : ''}
          placeholder="MM/DD/YYYY"
          onChange={(date: Date) => editDateHandler(date, 'start_dt')}
          wrapperClasses="flex flex-col w-full"
          fieldLabelClasses="hidden"
          error={startDateError && startDateError.reason}
          isError={!!startDateError}
          errorClassName={!codeError && !startDateError ? 'hidden' : undefined}
          closeOnScroll
        />
      </Truncatebale>
      <Truncatebale>
        <DatePicker
          value={moment(plan.end_dt).isValid() ? moment(plan.end_dt).format('MM/DD/YYYY') : ''}
          placeholder="MM/DD/YYYY"
          onChange={(date: Date) => editDateHandler(date, 'end_dt')}
          wrapperClasses="flex flex-col w-full"
          fieldLabelClasses="hidden"
          errorClassName={!codeError && !startDateError ? 'hidden' : undefined}
          closeOnScroll
        />
      </Truncatebale>

      {!disabledField && (
        <button
          onClick={onDelete}
          className="flex transform rotate-45 w-6 h-6 mt-4 justify-self-center items-center focus:outline-none active:outline-none hover:transform hover:scale-110"
        >
          <Add />
        </button>
      )}
    </div>
  );
};

export default EditPlans;
